// Iter67 — Manual CFM-CC-LEAPS / PMCC Scanner Panel.
//
// Read-only UI that lets the user POST a single JSON body to the
// Iter66 backend scanner:
//   POST /api/forge/toolbox/scanner/cfm-leaps-preview
// One backend call per click. No auto-fire on load, no multi-endpoint
// fanout, no broker, account, position, journal, DB, or live market
// fetch. No LEAPS exercise automation. No assignment automation.
//
// Cash Flow Machine LEAPS / Poor Man's Covered Call — synthetic
// inventory, NOT real shares. Mirrors the Iter58 (BPCS), Iter60
// (BCCS), Iter63 (Iron Condor) and Iter65 (CFM-Shares) panels —
// strictly isolated, never references any other module endpoint.
// Visual identity: violet/purple `#A855F7` to distinguish from BPCS
// (blue), BCCS (red), Wheel (green), Iron Condor (amber) and
// CFM-Shares (cyan).

import React, { useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ChevronDown, ChevronUp, Play, Search,
  ShieldAlert, CheckCircle2, Eraser,
} from "lucide-react";
import { API } from "@/App";
import ScannerResultActions from "@/components/ScannerResultActions";
import {
  ReasonCodeChips,
  ScannerWarnings,
  ScannerErrorMessage,
} from "@/components/ScannerResultNormalization";
import FriendlyScannerForm from "@/components/FriendlyScannerForm";
import StrategyResultCard from "@/components/StrategyResultCard";

// Single allowed endpoint — hardcoded, never derived from input.
const SCANNER_ENDPOINT = "/forge/toolbox/scanner/cfm-leaps-preview";

const DEFAULT_BODY = {
  watchlist: ["SPY"],
  payloads_by_symbol: {
    SPY: {
      spot_price: 500.0,
      wes: {
        val: 495.0, vah: 510.0, poc: 502.0,
        wes_p: 8, wes_c: 7, delta_VAL: 0.3,
        closed_bar_confirmed: true,
      },
      option_chains: {
        leaps_calls: [
          { type: "call", strike: 400.0, bid: 110.0, ask: 112.0,
            delta: 0.85, theta: -0.05, open_interest: 600,
            volume: 25, expiration: "2027-01-15", dte: 365 },
        ],
        short_calls: [
          { type: "call", strike: 500.0, bid: 3.20, ask: 3.30,
            delta: 0.50, theta: -0.14, open_interest: 2500,
            volume: 150, expiration: "2026-06-21", dte: 14 },
        ],
      },
      ivr: 35,
      account_context: {
        allow_leaps: true,
        total_short_call_credits: 0.0,
        assignment_policy: "manage_short_call",
      },
    },
  },
};

const STATUS_BADGE = {
  ASSIGNMENT_RISK:  "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  DEFEND_LEAPS:     "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  CLOSE_DIAGONAL:   "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  BUYBACK:          "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  ROLL_OUT_UP:      "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  ENTER:            "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  WATCH:            "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  BLOCKED:          "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  NO_TRADE:         "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
  ERROR:            "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
};

const COUNT_KEYS = [
  "enter", "watch", "buyback", "roll_out_up", "close_diagonal",
  "defend_leaps", "assignment_risk", "blocked", "no_trade", "error",
];

export default function CFMLeapsScannerPanel() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(JSON.stringify(DEFAULT_BODY, null, 2));
  const [parseErr, setParseErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);

  async function runScanner() {
    setErr(null);
    setParseErr(null);
    let parsed;
    try {
      parsed = JSON.parse(body || "{}");
    } catch (e) {
      setParseErr(`Invalid JSON: ${e.message}`);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}${SCANNER_ENDPOINT}`, parsed);
      setResult(r.data);
      setLastRequest(parsed);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }

  function resetBody() {
    setBody(JSON.stringify(DEFAULT_BODY, null, 2));
    setParseErr(null);
  }

  function clearResult() {
    setResult(null);
    setErr(null);
  }

  return (
    <div
      data-testid="cfm-leaps-scanner-panel"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        data-testid="cfm-leaps-scanner-toggle"
        onClick={() => setOpen(v => !v)}
        type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Search size={16} className="text-[#A855F7]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          Cash Flow Machine — LEAPS / PMCC Scanner
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · Iter66 backend · manual-run-per-click · Poor Man&apos;s Covered Call · synthetic inventory
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* ── Safety banner ───────────────────────────────────── */}
          <div
            data-testid="cfm-leaps-scanner-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#A855F7]/5 text-[#A855F7] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="cfm-leaps-scanner-banner-cfm-leaps-only">
              CFM-CC-LEAPS scanner only.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-synthetic-inventory">
              PMCC / LEAPS is synthetic inventory, not shares.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-readonly">
              Read-only opportunity discovery.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-no-execution">
              No trade execution.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-no-exercise-automation">
              No LEAPS exercise automation.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-no-assignment-automation">
              No assignment automation.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-prebuilt-payloads">
              Requires prebuilt payloads.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-no-broker-account-fetch">
              No broker/account live-data fetch.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-assignment-risk-diagnostic">
              Assignment risk is diagnostic only.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-readonly-management-states">
              Read-only management states.
            </span>
            <span className="text-[#A855F7]/50">·</span>
            <span data-testid="cfm-leaps-scanner-banner-pmcc-language">
              Poor Man&apos;s Covered Call · LEAPS extrinsic value · Theta carry ratio · Net delta · Diagonal defense.
            </span>
          </div>

          {/* ── Body editor + run controls ───────────────────────── */}
          <div className="px-4 py-3 border-b border-[#26262B]">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                Request body
              </span>
              <span className="text-[9px] font-mono text-[#5F5F66] truncate"
                    title={SCANNER_ENDPOINT}>
                {SCANNER_ENDPOINT}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  data-testid="cfm-leaps-scanner-reset"
                  onClick={resetBody}
                  className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                >
                  <Eraser size={10} /> Reset
                </button>
                <button
                  type="button"
                  data-testid="cfm-leaps-scanner-run"
                  onClick={runScanner}
                  disabled={loading}
                  className="px-3 py-1 rounded-sm border border-[#A855F7]/40 bg-[#A855F7]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#A855F7] hover:bg-[#A855F7]/20 disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <Play size={10} /> {loading ? "Scanning…" : "Run CFM LEAPS Scanner"}
                </button>
              </div>
            </div>

            <FriendlyScannerForm
              module="CFM_CC_LEAPS"
              body={body}
              setBody={setBody}
              parseErr={parseErr}
            />

            <details
              data-testid="cfm-leaps-scanner-advanced-payload"
              className="border border-[#26262B] rounded-sm"
            >
              <summary
                data-testid="cfm-leaps-scanner-advanced-summary"
                className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]"
              >
                Advanced / Developer payload (raw JSON)
              </summary>
              <textarea
                data-testid="cfm-leaps-scanner-payload"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                spellCheck={false}
                className="w-full bg-[#08080A] border-t border-[#26262B] p-2 text-[10px] font-mono text-[#E0E0E0] focus:outline-none focus:border-[#A855F7]/50 leading-relaxed"
                rows={10}
              />
            </details>

            {parseErr && (
              <div
                data-testid="cfm-leaps-scanner-parse-error"
                className="mt-2 flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
              >
                <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                <span>{parseErr}</span>
              </div>
            )}
          </div>

          {/* ── Result render ───────────────────────────────────── */}
          {loading && (
            <div
              data-testid="cfm-leaps-scanner-loading"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              Running CFM-CC-LEAPS scanner…
            </div>
          )}

          {err && !loading && (
            <div
              data-testid="cfm-leaps-scanner-error"
              className="px-4 py-3 flex items-start gap-2 text-[10px] font-mono text-[#FF3366]"
            >
              <AlertTriangle size={11} className="shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {!loading && !err && !result && (
            <div
              data-testid="cfm-leaps-scanner-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No scan run yet. Edit the request body above and click
              Run CFM LEAPS Scanner.
            </div>
          )}

          {result && !loading && !err && (
            <ScannerResult result={result} onClear={clearResult} lastRequest={lastRequest} />
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, testid, accent }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[#E0E0E0]"
    >
      <span className={`uppercase tracking-[0.12em] ${accent || "text-[#5F5F66]"}`}>
        {label}
      </span>
      <span className="font-bold">{String(value)}</span>
    </span>
  );
}

function ScannerResult({ result, onClear, lastRequest }) {
  const counts = result.counts || {};
  return (
    <div
      data-testid="cfm-leaps-scanner-result"
      className="px-4 py-3"
    >
      <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px] font-mono">
        <CheckCircle2 size={11} className="text-[#00FF88]" />
        <span
          data-testid="cfm-leaps-scanner-result-version"
          className="text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          {result.scanner_version}
        </span>
        <span
          data-testid="cfm-leaps-scanner-result-module-id"
          className="px-2 py-0.5 rounded-sm border border-[#A855F7]/40 bg-[#A855F7]/10 text-[9px] uppercase tracking-[0.14em] text-[#A855F7]"
        >
          {result.module_id}
        </span>
        <span
          data-testid="cfm-leaps-scanner-result-total"
          className="text-[#E0E0E0]"
        >
          {result.total_symbols} symbols scanned
        </span>
        <button
          type="button"
          data-testid="cfm-leaps-scanner-result-clear"
          onClick={onClear}
          className="ml-auto text-[#5F5F66] hover:text-[#E0E0E0] uppercase tracking-[0.12em]"
        >
          Clear
        </button>
        <ScannerResultActions
          result={result}
          request={lastRequest}
          endpoint={SCANNER_ENDPOINT}
          sourcePanel="CFMLeapsScannerPanel"
          moduleId={result.module_id || "CFM_CC_LEAPS"}
          extraFlags={{
            assignment_automation_enabled: false,
            exercise_automation_enabled: false,
          }}
          testidPrefix="cfm-leaps-scanner-result"
        />
      </div>

      <div className="flex flex-wrap gap-3 text-[10px] font-mono mb-3">
        {COUNT_KEYS.map(k => (
          <span
            key={k}
            data-testid={`cfm-leaps-scanner-result-count-${k}`}
            className="flex items-center gap-1 text-[#8A8A93]"
          >
            <span className="uppercase tracking-[0.12em]">{k}</span>
            <span className="font-bold text-[#E0E0E0]">{counts[k] ?? 0}</span>
          </span>
        ))}
      </div>

      {Array.isArray(result.warnings) && result.warnings.length > 0 && (
        <div className="mb-3">
          <ScannerWarnings
            warnings={result.warnings}
            testid="cfm-leaps-scanner-result-warnings"
          />
        </div>
      )}

      <ul className="space-y-2">
        {(result.results || []).map(rec => (
          <StrategyResultCard
            key={rec.symbol}
            rec={rec}
            moduleLabel="CFM-CC-LEAPS"
            accent={{
              border: "border-[#A855F7]/30",
              glow:   "bg-[#A855F7]/5",
              text:   "text-[#A855F7]",
            }}
            testidPrefix="cfm-leaps-scanner-result-row"
          >
            {(rec.assignment_risk === true ||
                rec.assignment_risk === "true") && (
              <div
                data-testid={`cfm-leaps-scanner-result-row-assignment-risk-${rec.symbol}`}
                className="mt-1 px-2 py-1 rounded-sm border border-[#FF3366]/40 bg-[#FF3366]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#FF3366] flex items-center gap-2"
                aria-label="Assignment risk diagnostic — short call at assignment risk vs the LEAPS"
              >
                <span>Assignment risk · diagnostic only</span>
              </div>
            )}
            <div
              data-testid={`cfm-leaps-scanner-result-row-automation-disabled-${rec.symbol}`}
              className="mt-1 flex flex-wrap gap-2 text-[9px] font-mono uppercase tracking-[0.14em] text-[#FFB800]"
            >
              <span>No LEAPS exercise automation</span>
              <span>· No assignment automation</span>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] font-mono">
              <Metric label="LEAPS intrinsic" value={rec.leaps_intrinsic}
                       testid={`cfm-leaps-scanner-result-row-leaps-intrinsic-${rec.symbol}`} />
              <Metric label="LEAPS extrinsic" value={rec.leaps_extrinsic}
                       testid={`cfm-leaps-scanner-result-row-leaps-extrinsic-${rec.symbol}`} />
              <Metric label="Short intrinsic" value={rec.short_call_intrinsic}
                       testid={`cfm-leaps-scanner-result-row-short-intrinsic-${rec.symbol}`} />
              <Metric label="Short extrinsic" value={rec.short_call_extrinsic}
                       testid={`cfm-leaps-scanner-result-row-short-extrinsic-${rec.symbol}`} />
              <Metric label="Adj basis" value={rec.adjusted_leaps_basis}
                       testid={`cfm-leaps-scanner-result-row-adjusted-basis-${rec.symbol}`} />
              <Metric label="Standalone BE" value={rec.leaps_standalone_breakeven}
                       testid={`cfm-leaps-scanner-result-row-standalone-be-${rec.symbol}`} />
              <Metric label="Campaign value" value={rec.current_campaign_value}
                       testid={`cfm-leaps-scanner-result-row-campaign-value-${rec.symbol}`} />
              <Metric label="Campaign P&L" value={rec.campaign_pnl}
                       testid={`cfm-leaps-scanner-result-row-campaign-pnl-${rec.symbol}`} />
              <Metric label="Net delta" value={rec.net_delta}
                       testid={`cfm-leaps-scanner-result-row-net-delta-${rec.symbol}`} />
              <Metric label="Theta carry" value={rec.theta_carry_ratio}
                       testid={`cfm-leaps-scanner-result-row-theta-carry-${rec.symbol}`} />
            </div>
          </StrategyResultCard>
        ))}
      </ul>
    </div>
  );
}
